import { useEffect } from 'react';

/**
 * The panel's two keyboard shortcuts: `A` toggles Activity, `Esc` collapses.
 *
 * ## Why this is a hook and not four lines in the component
 *
 * A BARE-LETTER shortcut is the hostile case. Both host pages are full of free text — a picklist item memo,
 * an adjustment reason, the receiving cancel-reason textarea — and a naive listener steals the letter `A`
 * from every one of them. The repo has two different, mutually inconsistent guards for this already
 * (`QuotationMasterList` lowercases `tagName` and checks contentEditable; `SalesOrderReadinessChecklist`
 * reads `document.activeElement` and compares UPPERCASE) and neither is shared. This is the third site, so
 * it is written once, here, with the union of both guard sets.
 *
 * ## The overlay problem, which is the part that bites
 *
 * `Esc` is claimed by every Radix overlay on these pages — 5 inline dialogs plus two more on Picklist, 3
 * plus the correction sheet on Receiving. Radix does not `preventDefault` the raw document keydown in every
 * case, so a naive listener closes the panel BEHIND a modal the user is still looking at. Worse, one picklist
 * dialog is deliberately un-dismissable while a post is in flight — there, `Esc` is a no-op for the dialog
 * and would still reach us.
 *
 * So the bail-out is three-part, and each part catches a different family:
 *   - `defaultPrevented` — anything that handled the key properly
 *   - an open Radix popper (`[data-radix-popper-content-wrapper]`) — Select, Popover, DropdownMenu, Tooltip
 *   - an open Radix dialog (`[role="dialog"][data-state="open"]`) — Dialog, AlertDialog, Sheet
 *
 * The popper check alone is the repo's existing idiom (`FullscreenReviewShell`), and it does NOT cover
 * dialogs — which is most of what is mounted on these two pages.
 */
export interface SectionHotkeyHandlers {
  /** `A` — toggle the Activity section open/closed. */
  onToggleActivity: () => void;
  /** `Esc` — collapse, whatever is open. No-op when already collapsed. */
  onCollapse: () => void;
  /** Skip everything while false, so a hidden panel never listens. */
  enabled?: boolean;
}

export function useSectionHotkeys({
  onToggleActivity,
  onCollapse,
  enabled = true,
}: SectionHotkeyHandlers) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      // A modifier means the user is aiming at the browser or the OS, not at us. Ctrl+A is Select All.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) return;

      // An overlay owns the keyboard while it is open. Checked on the DOCUMENT rather than on the event
      // target because a dialog portals to the body — the target may be nowhere near it.
      if (
        document.querySelector('[data-radix-popper-content-wrapper]') ||
        document.querySelector('[role="dialog"][data-state="open"]')
      ) {
        return;
      }

      if (event.key === 'Escape') {
        onCollapse();
        return;
      }

      // `key`, not `code`: a Dvorak or AZERTY user pressing the key that PRODUCES an "a" is who this is for.
      if (event.key === 'a' || event.key === 'A') {
        event.preventDefault();
        onToggleActivity();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, onToggleActivity, onCollapse]);
}

/**
 * Whether the element is somewhere a keystroke means text rather than a command.
 *
 * Checks BOTH `event.target` and `document.activeElement` at the call site above, because they disagree in
 * the case that matters: a keystroke inside a portalled overlay can report a target outside the field that
 * actually has focus.
 */
function isTypingTarget(node: EventTarget | Element | null): boolean {
  if (!(node instanceof HTMLElement)) return false;

  const tag = node.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    node.isContentEditable
  );
}
